import React from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, X, ListMusic, Trash2, Play, Pause } from "lucide-react";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePaywall } from "@/contexts/PaywallContext";
import { mediumArt, formatTime } from "@/lib/api";
import SmartImage from "@/components/SmartImage";

// ---- Sortable row ----
const QueueRow = ({ track, index, isActive, isPlaying, onPlay, onRemove }) => {
  const id = `q-${track.trackId || index}-${index}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`queue-row-${index}`}
      className={`group relative flex items-center gap-3 px-3 py-2 rounded-lg border transition-all ${
        isActive
          ? "bg-[#FF2E00]/10 border-[#FF2E00]/40"
          : "bg-white/[0.02] border-white/5 hover:bg-white/[0.05]"
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        data-testid={`queue-drag-${index}`}
        className="touch-none cursor-grab active:cursor-grabbing text-white/30 hover:text-white/60 transition shrink-0"
        title="Drag to reorder"
        type="button"
      >
        <GripVertical size={16} />
      </button>

      {/* Cover */}
      <div className="relative w-10 h-10 rounded-md overflow-hidden shrink-0 bg-white/5">
        <SmartImage
          src={mediumArt(track.artworkUrl100)}
          alt={track.trackName || ""}
          seed={track.trackName || track.artistName || "?"}
          className="absolute inset-0 w-full h-full"
          imgClassName="w-full h-full object-cover"
          tileStyle={{ borderRadius: 0 }}
        />
        <button
          onClick={onPlay}
          data-testid={`queue-play-${index}`}
          className="absolute inset-0 bg-black/0 group-hover:bg-black/50 flex items-center justify-center transition opacity-0 group-hover:opacity-100"
          type="button"
        >
          {isActive && isPlaying ? (
            <Pause size={14} fill="currentColor" />
          ) : (
            <Play size={14} fill="currentColor" className="ml-0.5" />
          )}
        </button>
      </div>

      {/* Title */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium truncate ${
            isActive ? "text-[#FF2E00]" : "text-white"
          }`}
        >
          {track.trackName}
        </p>
        <p className="text-xs text-white/45 truncate">{track.artistName}</p>
      </div>

      {/* Duration */}
      <span className="text-[11px] text-white/40 font-mono tabular-nums hidden sm:inline shrink-0">
        {track.trackTimeMillis ? formatTime(track.trackTimeMillis) : ""}
      </span>

      {/* Remove */}
      <button
        onClick={onRemove}
        data-testid={`queue-remove-${index}`}
        className="p-1.5 rounded-full hover:bg-white/10 text-white/40 hover:text-[#FF2E00] transition opacity-0 group-hover:opacity-100 focus:opacity-100 shrink-0"
        title="Remove from queue"
        type="button"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const QueuePanel = ({ open, onClose }) => {
  const {
    queue, index, current, playing,
    playAt, togglePlay, removeFromQueue, reorderQueue, clearQueue,
  } = usePlayer();
  const { premium } = useAuth();
  const { showPaywall } = usePaywall();
  const isPremium = !!premium?.active;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!open) return null;

  const upcoming = queue.slice(Math.max(index + 1, 0));
  const upcomingStart = Math.max(index + 1, 0);
  const ids = upcoming.map((t, i) => `q-${t.trackId || i + upcomingStart}-${i + upcomingStart}`);

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    // Queue reorder is a Premium feature — block free users at the
    // commit step (we still let the drag visualisation play out so it
    // doesn't feel like the gesture broke).
    if (!isPremium) {
      showPaywall("queue-reorder");
      return;
    }
    const oldRel = ids.indexOf(active.id);
    const newRel = ids.indexOf(over.id);
    if (oldRel < 0 || newRel < 0) return;
    reorderQueue(upcomingStart + oldRel, upcomingStart + newRel);
  };

  return (
    <div
      data-testid="queue-panel"
      className="fixed inset-0 z-[55] flex justify-end bg-black/50 backdrop-blur-sm animate-in fade-in duration-150"
      onClick={onClose}
    >
      <aside
        className="relative w-full sm:w-[440px] h-full bg-gradient-to-b from-[#161616] via-[#0d0d0d] to-black border-l border-white/10 shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#E2FF31] font-bold mb-0.5 flex items-center gap-1.5">
              <ListMusic size={12} /> Queue
            </p>
            <h2 className="font-display text-xl font-semibold">
              Up next ({upcoming.length})
            </h2>
          </div>
          <div className="flex items-center gap-1">
            {queue.length > 0 && (
              <button
                onClick={clearQueue}
                data-testid="queue-clear-btn"
                className="p-2 rounded-full hover:bg-white/10 text-white/50 hover:text-[#FF2E00] transition"
                title="Clear queue"
                type="button"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              onClick={onClose}
              data-testid="queue-close-btn"
              className="p-2 rounded-full hover:bg-white/10 transition"
              title="Close"
              type="button"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Now playing */}
        {current && (
          <div className="px-5 py-3 border-b border-white/5 shrink-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-2">
              Now playing
            </p>
            <div
              data-testid="queue-now-playing"
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#FF2E00]/10 border border-[#FF2E00]/30"
            >
              <div className="relative w-11 h-11 rounded-md overflow-hidden shrink-0 bg-white/5">
                <SmartImage
                  src={mediumArt(current.artworkUrl100)}
                  alt={current.trackName || ""}
                  seed={current.trackName || current.artistName || "?"}
                  className="absolute inset-0 w-full h-full"
                  imgClassName="w-full h-full object-cover"
                  tileStyle={{ borderRadius: 0 }}
                />
                <button
                  onClick={togglePlay}
                  data-testid="queue-now-playing-toggle"
                  className="absolute inset-0 bg-black/0 hover:bg-black/40 flex items-center justify-center transition"
                  type="button"
                >
                  {playing ? (
                    <Pause size={14} fill="currentColor" />
                  ) : (
                    <Play size={14} fill="currentColor" className="ml-0.5" />
                  )}
                </button>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate text-[#FF2E00]">{current.trackName}</p>
                <p className="text-xs text-white/55 truncate">{current.artistName}</p>
              </div>
            </div>
          </div>
        )}

        {/* Up next list */}
        <div className="flex-1 overflow-y-auto px-3 py-3 hide-scrollbar">
          {upcoming.length === 0 ? (
            <div className="text-center py-16 px-4">
              <ListMusic size={32} className="mx-auto text-white/20 mb-3" />
              <p className="text-sm text-white/50">
                {current ? "Nothing queued after this." : "Your queue is empty."}
              </p>
              <p className="text-xs text-white/30 mt-1">
                Tap any track on the app to start playback.
              </p>
            </div>
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
              <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                <div className="space-y-1.5">
                  {upcoming.map((t, i) => {
                    const absIndex = upcomingStart + i;
                    return (
                      <QueueRow
                        key={ids[i]}
                        track={t}
                        index={absIndex}
                        isActive={absIndex === index}
                        isPlaying={playing}
                        onPlay={() => {
                          if (absIndex === index) togglePlay();
                          else playAt(absIndex, queue);
                        }}
                        onRemove={() => removeFromQueue(absIndex)}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </aside>
    </div>
  );
};

export default QueuePanel;
